<template>
  <info-box
    class="loanable-info-box"
    :loading="loading"
    :big-img="loanable.image"
    :link="`/loanables/${loanable.id}`"
    :title="loanable.name"
    :avatar="contextImage"
    :avatar-fallback="context"
  >
    <b-row class="flex-fill align-items-center" no-gutters align-h="between">
      <b-col cols="12" xl="8">
        <span v-if="context" class="loanable-info-box__owner">{{ context }}<br /></span>
        <span class="loanable-info-box__name">{{ loanable.name }}</span>

        <div class="mt-2">
          <availability-status :loanable="loanable" />
        </div>
      </b-col>

      <b-col class="loanable-info-box__actions mt-2 mt-xl-0" cols="12" xl="4">
        <slot name="buttons">
          <icon-button
            v-if="hasButton('availability')"
            size="sm"
            :disabled="loading"
            variant="ghost-secondary"
            :to="`/profile/loanables/${loanable.id}#availability`"
            icon="calendar4-range"
          >
            {{ $t("components.loanable.info_box.buttons.modify_availability") }}
          </icon-button>

          <icon-button
            v-if="hasButton('remove') && canDeleteLoanable"
            size="sm"
            :disabled="loading"
            variant="outline-danger"
            role="remove-item"
            @click.prevent="disableLoanableModal"
          >
            {{ $t("forms.remove") | capitalize }}
          </icon-button>
        </slot>
      </b-col>
    </b-row>
  </info-box>
</template>

<script>
import AvailabilityStatus from "@/components/Loanable/AvailabilityStatus.vue";
import IconButton from "@/components/shared/IconButton.vue";
import InfoBox from "@/components/shared/InfoBox.vue";
import { capitalize } from "@/helpers/filters";
import { canDeleteLoanable, getOwner } from "@/helpers/permissions/loanables";

export default {
  name: "LoanableInfoBox",
  components: { IconButton, AvailabilityStatus, InfoBox },
  props: {
    buttons: {
      type: Array,
      required: false,
      default() {
        return ["availability", "remove"];
      },
    },
    loanable: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      canDeleteLoanable: canDeleteLoanable(this.$store.state.user, this.loanable),
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    context() {
      if (this.loanable.library) {
        return this.loanable.library.name;
      }
      if (this.loanableOwnerUser && this.loanableOwnerUser.id !== this.user.id) {
        return this.loanableOwnerUser.full_name;
      }
      return null;
    },
    contextImage() {
      if (this.loanable.library) {
        return this.loanable.library.avatar;
      }
      if (this.loanableOwnerUser && this.loanableOwnerUser.id !== this.user.id) {
        return this.loanableOwnerUser.avatar;
      }
      return null;
    },
    loanableOwnerUser() {
      return getOwner(this.loanable);
    },
  },
  methods: {
    disableLoanableModal() {
      this.$bvModal
        .msgBoxConfirm(this.$t("components.loanable.info_box.confirmation.title"), {
          size: "sm",
          buttonSize: "sm",
          okTitle: this.$t("components.loanable.info_box.confirmation.confirm"),
          cancelTitle: capitalize(this.$t("forms.cancel")),
          okVariant: "danger",
          cancelVariant: "ghost-secondary",
          footerClass: "p-2 border-top-0",
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.disableLoanable();
          }
        });
    },
    async disableLoanable() {
      this.loading = true;
      try {
        await this.$store.dispatch("loanables/disable", this.loanable.id);
        this.$emit("disabled", this.loanable.id);
        this.$store.dispatch("dashboard/loadLoanables");
      } finally {
        this.loading = false;
      }
    },
    hasButton(name) {
      return this.buttons.indexOf(name) > -1;
    },
  },
};
</script>

<style lang="scss">
@import "~bootstrap/scss/mixins/breakpoints";

.loanable-info-box {
  &__name {
    color: $black;
    font-size: 1.25rem;
    position: unset;
  }

  &__owner {
    color: $black;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    @include media-breakpoint-up(xl) {
      justify-content: end;
    }
  }
}
</style>
